import { createSelector } from 'reselect';
import { undefIfEmpty } from 'utils/validation';
import { emptyArray } from '../../constants';
export const strings = {
    friendsOnEmma: 'Friends on Emma',
    emmaUsers: 'Emma users',
    otherContacts: 'Other contacts',
};
const selectEmmaUser = (store) => store.contacts.alreadyEmmaUsers;
const selectContactsWithFriends = (store) => store.contacts.contactsWithFriends;
const selectCachedContacts = (store) => store.contacts.contactsCache;
export const selectSentContacts = (store) => store.contacts.sentContacts;
const getNameToShow = (contact) => {
    const { givenName, familyName, company } = contact;
    const firstName = undefIfEmpty(givenName);
    const lastName = undefIfEmpty(familyName);
    const companyName = undefIfEmpty(company);
    if (firstName && lastName)
        return `${firstName} ${lastName}`;
    if (firstName)
        return `${firstName}`;
    if (lastName)
        return `${lastName}`;
    if (companyName)
        return `${companyName}`;
    return undefined;
};
// Like getNameToShow, but never joins first and last name. For copy such as
// "Send money to <name>" and avatar initials.
export const getFirstNameToShow = (contact) => {
    const { givenName, familyName, company } = contact;
    return (undefIfEmpty(givenName) ?? undefIfEmpty(familyName) ?? undefIfEmpty(company));
};
// The last name to pair with getFirstNameToShow. Without a first name,
// getFirstNameToShow already returns the family name, so this returns
// undefined rather than showing it twice ("Smith Smith", initials "SS").
export const getLastNameToShow = (contact) => undefIfEmpty(contact.givenName)
    ? undefIfEmpty(contact.familyName)
    : undefined;
const sort = (a, b) => {
    if (a.nameToShow && b.nameToShow) {
        if (a.nameToShow < b.nameToShow)
            return -1;
        if (a.nameToShow > b.nameToShow)
            return 1;
    }
    return 0;
};
const splitContacts = (contacts, emmaUsers, contactsWithFriends) => {
    if (!contacts.length)
        return [[], []];
    if (!Object.keys(emmaUsers).length)
        return [contacts, []];
    const pureContacts = [];
    const emmaContacts = [];
    contacts.forEach((contact) => {
        const handleInfo = emmaUsers[contact.recordID];
        const nameToShow = getNameToShow(contact);
        if (handleInfo) {
            emmaContacts.push({
                ...contact,
                hasThumbnail: handleInfo.imageUrl !== undefined || contact.hasThumbnail,
                thumbnailPath: handleInfo.imageUrl ?? contact.thumbnailPath,
                handle: handleInfo.handle,
                isEmmaUser: true,
                nameToShow,
                hasSubscription: handleInfo.hasSubscription,
            });
        }
        else {
            pureContacts.push({
                ...contact,
                isEmmaUser: false,
                friendsCount: contactsWithFriends[contact.recordID]?.count,
                nameToShow,
            });
        }
    });
    return [pureContacts.sort(sort), emmaContacts.sort(sort)];
};
const searchContacts = (searchFilter, contacts) => {
    if (!searchFilter.length)
        return contacts;
    if (contacts.length > 0) {
        const wordsToMatch = searchFilter.toLowerCase().split(' ');
        return contacts.filter((contact) => wordsToMatch.every((word) => contact.handle?.toLowerCase().startsWith(word) ||
            `@${contact.handle}`?.toLowerCase().startsWith(word) ||
            contact.givenName?.toLowerCase().startsWith(word) ||
            contact.familyName?.toLowerCase().startsWith(word) ||
            contact.phoneNumbers?.find((it) => it.number.toLowerCase().startsWith(word))));
    }
    return emptyArray;
};
const mapEmmaUsers = (contacts, emmaUsers, contactsWithFriends) => {
    if (!contacts.length)
        return emptyArray;
    const contactsList = [];
    contacts.forEach((contact) => {
        const handleInfo = emmaUsers[contact.recordID];
        const nameToShow = getNameToShow(contact);
        if (handleInfo) {
            contactsList.push({
                ...contact,
                hasThumbnail: handleInfo.imageUrl !== undefined || contact.hasThumbnail,
                thumbnailPath: handleInfo.imageUrl ?? contact.thumbnailPath,
                handle: handleInfo.handle,
                isEmmaUser: true,
                nameToShow,
            });
        }
        else {
            contactsList.push({
                ...contact,
                isEmmaUser: false,
                friendsCount: contactsWithFriends[contact.recordID]?.count,
                nameToShow,
            });
        }
    });
    // sort by name
    return contactsList.sort(sort);
};
// without split emmausers / other contacts
const contactsListToShow = (contacts, emmaUsers, contactsWithFriends) => {
    const contactsList = Object.keys(emmaUsers).length > 0
        ? mapEmmaUsers(contacts, emmaUsers, contactsWithFriends)
        : contacts;
    return contactsList;
};
const mapToSections = (searchFilter, contacts, remoteContacts, emmaUsers, contactsWithFriends) => {
    const [pureContacts, emmaContacts] = Object.keys(emmaUsers).length > 0
        ? splitContacts(contacts, emmaUsers, contactsWithFriends)
        : [contacts, []];
    const foundEmmaContacts = searchContacts(searchFilter, emmaContacts);
    // all handles in local contacts as an object map
    const localHandles = foundEmmaContacts
        .map((it) => it.handle)
        .reduce((prev, next) => (next ? { ...prev, [next]: true } : prev), {});
    // remote contacts excluding the ones with the local handles
    const foundRemoteContacts = remoteContacts.filter((it) => it.handle && !localHandles[it.handle]);
    const friendsSection = foundEmmaContacts.length > 0
        ? [
            {
                title: strings.friendsOnEmma,
                data: foundEmmaContacts,
            },
        ]
        : [];
    const emmaSection = foundRemoteContacts.length > 0
        ? [
            {
                title: strings.emmaUsers,
                data: foundRemoteContacts,
            },
        ]
        : [];
    const foundPureContacts = searchContacts(searchFilter, pureContacts);
    const contactsSection = foundPureContacts.length > 0
        ? [{ title: strings.otherContacts, data: foundPureContacts }]
        : [];
    const data = [...emmaSection, ...friendsSection, ...contactsSection];
    const indexedData = data.map((eachData, index) => ({
        ...eachData,
        index,
    }));
    return indexedData;
};
export const selectSections = createSelector([
    selectEmmaUser,
    selectContactsWithFriends,
    selectCachedContacts,
    (_state, searchFilter, remoteContacts) => ({
        searchFilter,
        remoteContacts,
    }),
], (emmaUsers, contactsWithFriends, contactsCache, { searchFilter, remoteContacts }) => mapToSections(searchFilter, contactsCache, remoteContacts, emmaUsers, contactsWithFriends));
export const selectContactsInOrder = createSelector([
    selectEmmaUser,
    selectContactsWithFriends,
    selectCachedContacts,
    (_state, searchFilter) => searchFilter,
], (emmaUsers, contactsWithFriends, contactsCache, searchFilter) => {
    const contactsList = contactsListToShow(contactsCache, emmaUsers, contactsWithFriends);
    return searchContacts(searchFilter, contactsList);
});
export const selectEmmaUsersAsContacts = createSelector([selectEmmaUser, selectContactsWithFriends, selectCachedContacts], (emmaUsers, contactsWithFriends, contactsCache) => {
    const contactsList = contactsListToShow(contactsCache, emmaUsers, contactsWithFriends).filter((contact) => contact.handle !== undefined);
    return contactsList;
});
